import { StandardEvents } from '@shopify/events';
import { morph, MORPH_OPTIONS } from '@theme/morph';

class ThemeCartRecommendations extends HTMLElement {
  #controller;
  #listeners;
  #revision = 0;
  #resize;
  #drag;
  #dragged = false;

  connectedCallback() {
    this.#listeners = new AbortController();
    const options = { signal: this.#listeners.signal };
    this.addEventListener('click', this.#onClick, options);
    this.querySelector('.cart-suggestions__track').addEventListener('scroll', this.#updateArrows, options);
    this.addEventListener('pointerdown', this.#onPointerDown, options);
    this.addEventListener('pointermove', this.#onPointerMove, options);
    this.addEventListener('pointerup', this.#onPointerEnd, options);
    this.addEventListener('pointercancel', this.#onPointerEnd, options);
    this.addEventListener('dragstart', (event) => event.preventDefault(), options);
    this.addEventListener('click', (event) => {
      if (!this.#dragged) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      this.#dragged = false;
    }, { ...options, capture: true });
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate, options);
    this.#resize = new ResizeObserver(this.#updateArrows);
    this.#resize.observe(this);
    // Read the current cart even when the page HTML was restored from cache.
    this.#onCartUpdate({});
  }

  disconnectedCallback() {
    this.#revision++;
    this.#controller?.abort();
    this.#listeners?.abort();
    this.#resize?.disconnect();
  }

  #onCartUpdate = async (event) => {
    const revision = ++this.#revision;
    this.#controller?.abort();
    // Keep the last valid cards and drawer width while the cart is unresolved.
    try {
      await event.promise;
      if (!this.isConnected || revision !== this.#revision) return;
      this.#controller = new AbortController();
      const response = await fetch(this.dataset.cartUrl, { signal: AbortSignal.any([this.#controller.signal, AbortSignal.timeout(8000)]), cache: 'no-store' });
      if (!response.ok) return;
      const cart = await response.json();
      if (!this.isConnected || revision !== this.#revision) return;
      await this.#load([...new Set(cart.items.map((item) => String(item.product_id)))]);
    } catch (error) {
      if (error?.name !== 'AbortError') console.warn('[cart-recommendations] Cart refresh unavailable.');
    }
  };

  async #load(seeds) {
    const revision = ++this.#revision;
    this.#controller?.abort();
    this.#controller = new AbortController();
    const signal = this.#controller.signal;
    const track = this.querySelector('.cart-suggestions__track');
    if (!seeds.length) {
      track.replaceChildren();
      this.hidden = true;
      return;
    }
    // A confirmed cart can invalidate existing cards, but loading alone cannot.
    const seen = new Set(seeds);
    for (const card of track.querySelectorAll('[data-theme-recommendation]')) {
      if (seen.has(card.dataset.productId)) card.remove();
    }
    this.hidden = !track.children.length;
    this.#updateArrows();

    // Promise.all preserves cart seed order even when responses arrive out of order.
    const responses = await Promise.all(seeds.map(async (id) => {
      const controller = new AbortController();
      const abort = () => controller.abort();
      signal.addEventListener('abort', abort, { once: true });
      const timeout = setTimeout(abort, 8000);
      try {
        const url = new URL(this.dataset.url, location.origin);
        url.searchParams.set('section_id', 'cart-suggestions');
        url.searchParams.set('product_id', id);
        url.searchParams.set('intent', 'complementary');
        url.searchParams.set('limit', '10'); // Shopify endpoint maximum per seed, not a combined carousel cap.
        const response = await fetch(url, { signal: controller.signal });
        if (!response.ok) return null;
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        return [...doc.querySelectorAll('[data-theme-recommendation]')];
      } catch {
        return null;
      } finally {
        clearTimeout(timeout);
        signal.removeEventListener('abort', abort);
      }
    }));
    if (!this.isConnected || signal.aborted || revision !== this.#revision) return;
    const cards = [];
    for (const card of responses.filter((response) => response !== null).flat()) {
      if (seen.has(card.dataset.productId)) continue;
      seen.add(card.dataset.productId);
      cards.push(document.importNode(card, true));
    }
    // Failed responses cannot confirm an empty result. Keep the pruned prior
    // cards unless replacements exist or every request successfully completed.
    if (!cards.length && responses.includes(null)) return;
    const next = track.cloneNode(false);
    next.append(...cards);
    // Refresh matching products in place so a focused link or Add is not removed.
    const focused = track.contains(document.activeElement) ? document.activeElement : null;
    morph(track, next, {
      ...MORPH_OPTIONS,
      getNodeKey: (node) => node instanceof HTMLElement ? node.dataset.productId || node.id : undefined,
    });
    if (focused instanceof HTMLElement && focused.isConnected && document.activeElement !== focused) {
      focused.focus({ preventScroll: true });
    }
    this.hidden = !track.children.length;
    track.scrollLeft = 0;
    this.#updateArrows();
  }

  #onClick = (event) => {
    const button = event.target.closest('button[data-direction]');
    if (!button) return;
    const track = this.querySelector('.cart-suggestions__track');
    track.scrollBy({ left: Number(button.dataset.direction) * (track.clientWidth + 8), behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
  };

  #onPointerDown = (event) => {
    this.#dragged = false;
    const track = event.target.closest('.cart-suggestions__track');
    if (!track || event.pointerType !== 'mouse' || event.button !== 0 || event.target.closest('button')) return;
    this.#drag = { x: event.clientX, scroll: track.scrollLeft, track };
  };

  #onPointerMove = (event) => {
    if (!this.#drag) return;
    const delta = event.clientX - this.#drag.x;
    if (!this.#dragged && Math.abs(delta) < 6) return;
    this.#dragged = true;
    this.#drag.track.setPointerCapture(event.pointerId);
    this.#drag.track.style.scrollSnapType = 'none';
    this.#drag.track.scrollLeft = this.#drag.scroll - delta;
    event.preventDefault();
  };

  #onPointerEnd = () => {
    if (this.#drag) this.#drag.track.style.removeProperty('scroll-snap-type');
    this.#drag = null;
  };

  #updateArrows = () => {
    const track = this.querySelector('.cart-suggestions__track');
    const previous = this.querySelector('[data-direction="-1"]');
    const next = this.querySelector('[data-direction="1"]');
    const previousDisabled = track.scrollLeft < 1;
    const nextDisabled = track.scrollLeft + track.clientWidth >= track.scrollWidth - 1;
    const focused = document.activeElement;

    // Disabling the focused arrow drops keyboard focus to the document body.
    // Move it before disabling, including when a resize removes all overflow.
    if ((focused === previous && previousDisabled) || (focused === next && nextDisabled)) {
      const replacement = previousDisabled ? next : previous;
      if (previousDisabled && nextDisabled) {
        track.focus({ preventScroll: true });
      } else {
        replacement.disabled = false;
        replacement.focus({ preventScroll: true });
      }
    }
    previous.disabled = previousDisabled;
    next.disabled = nextDisabled;
  };
}

if (!customElements.get('cart-suggestions')) customElements.define('cart-suggestions', ThemeCartRecommendations);
